import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import SendInvoiceModal from '../components/SendInvoiceModal';

const SEARCH_FIELDS = [
  { key:'stockNum',      label:'Stock Number',   icon:'🔢', placeholder:'e.g. 40383C' },
  { key:'poNumber',      label:'PO Number',      icon:'📄', placeholder:'e.g. PO-12345' },
  { key:'invoiceNumber', label:'Invoice Number',  icon:'🧾', placeholder:'e.g. INV-001' },
  { key:'rego',          label:'Rego',           icon:'🚗', placeholder:'e.g. 1ABC234' },   // also searches walk-ins
];

export default function JobSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();


  const [field,     setField]     = useState('stockNum');
  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState([]);
  const [walkIns,   setWalkIns]   = useState([]);
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [searched,  setSearched]  = useState(false);
  const [tab,       setTab]       = useState('jobs');   // which group is showing when both have results
  const [error,     setError]     = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true); setError(null);
    try {
      const [jobsRes, walkRes] = await Promise.all([
        api.get('/jobs/search', { params:{ q:query.trim(), field } }),
        field === 'rego' ? api.get('/walkins/search', { params:{ q:query.trim() } }) : Promise.resolve(null),
      ]);
      const jobs = jobsRes.data.jobs, walks = walkRes ? walkRes.data.walkIns : [];
      setResults(jobs); setWalkIns(walks); setSearched(true);
      setTab(jobs.length > 0 ? 'jobs' : 'walkins');   // open on the first group that has something
    } catch (err) {
      setError(err.response?.data?.message || 'Search failed');
    } finally { setLoading(false); }
  };

  const STATUS_BADGE = {
    submitted:   { bg:'rgba(22,163,74,0.1)',   color:'var(--green)', label:'Submitted'  },
    pending_po:  { bg:'rgba(217,119,6,0.1)',   color:'var(--amber)', label:'Pending PO' },
    po_received: { bg:'rgba(0,119,168,0.1)',   color:'var(--blue)', label:'PO Received'},
    invoiced:    { bg:'rgba(22,163,74,0.1)',   color:'var(--green)', label:'Invoiced'   },
  };

  return (
    <div style={{minHeight:'100vh',background:'var(--bg)',fontFamily:'Inter,sans-serif'}}>
      {/* Header */}
      <div style={{background:'var(--surface)',borderBottom:'1px solid var(--border)',padding:'12px 24px',display:'flex',alignItems:'center',gap:'12px',}}>
        <button onClick={()=>navigate(-1)} style={backBtn}>← Back</button>
        <div>
          <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'16px'}}>🔍 Job Search</div>
          <div style={{color:'var(--muted)',fontSize:'11px'}}>Search across all jobs</div>
        </div>
      </div>

      <div style={{maxWidth:'800px',margin:'0 auto',padding:'24px 16px'}}>

        {/* Search type selector */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:'10px',marginBottom:'20px'}}>
          {SEARCH_FIELDS.map(f=>(
            <button key={f.key} type="button" onClick={()=>{setField(f.key);setResults([]);setWalkIns([]);setSearched(false);setQuery('');}}
              style={{padding:'14px 10px',borderRadius:'var(--radius-lg)',border:`1.5px solid ${field===f.key?'var(--blue)':'var(--border-md)'}`,background:field===f.key?'rgba(0,119,168,0.08)':'#fff',cursor:'pointer',fontFamily:'inherit',transition:'all .15s',textAlign:'center'}}>
              <div style={{fontSize:'1.4rem',marginBottom:'6px'}}>{f.icon}</div>
              <div style={{fontSize:'13px',fontWeight:field===f.key?700:400,color:field===f.key?'var(--blue)':'var(--muted)'}}>{f.label}</div>
            </button>
          ))}
        </div>

        {/* Search input */}
        <form onSubmit={handleSearch} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'20px',marginBottom:'20px',boxShadow:'var(--shadow-sm)'}}>
          <div style={{fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'.8px',color:'var(--muted)',marginBottom:'8px'}}>
            Search by {SEARCH_FIELDS.find(f=>f.key===field)?.label}
          </div>
          <div style={{display:'flex',gap:'10px'}}>
            <input value={query} onChange={e=>setQuery(e.target.value.toUpperCase())}
              style={{flex:1,background:'var(--bg)',border:'1px solid var(--border-md)',borderRadius:'var(--radius)',padding:'11px 14px',color:'var(--text)',fontFamily:'JetBrains Mono,monospace',fontSize:'16px',fontWeight:'500',letterSpacing:'1px',outline:'none'}}
              placeholder={SEARCH_FIELDS.find(f=>f.key===field)?.placeholder}
              autoFocus/>
            <button type="submit" disabled={loading||!query.trim()} style={{background:loading||!query.trim()?'var(--border-md)':'var(--blue)',border:'none',borderRadius:'var(--radius)',padding:'11px 24px',color:'#fff',fontWeight:'500',fontSize:'14px',cursor:loading||!query.trim()?'not-allowed':'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
              {loading?'Searching...':'🔍 Search'}
            </button>
          </div>
        </form>

        {error && <div style={{background:'var(--red-bg)',border:'1px solid rgba(220,38,38,0.3)',borderRadius:'var(--radius)',padding:'12px 16px',color:'var(--red)',fontSize:'13px',marginBottom:'16px'}}>⚠️ {error}</div>}

        {/* Results */}
        {/* Rego search finds yard jobs AND walk-ins: only the groups with results are shown, as tabs when both have some */}
        {searched && field === 'rego' && results.length > 0 && walkIns.length > 0 && (
          <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
            {[['jobs','Yard jobs',results.length],['walkins','Walk-ins',walkIns.length]].map(([key,label,n])=>(
              <button key={key} type="button" onClick={()=>setTab(key)}
                style={{padding:'9px 16px',borderRadius:'999px',border:`1.5px solid ${tab===key?'var(--blue)':'var(--border-md)'}`,background:tab===key?'rgba(0,119,168,0.08)':'#fff',color:tab===key?'var(--blue)':'var(--muted)',fontWeight:tab===key?600:400,fontSize:'13px',cursor:'pointer',fontFamily:'inherit'}}>
                {label} <span style={{opacity:.7}}>({n})</span>
              </button>
            ))}
          </div>
        )}

        {searched && (field !== 'rego' || (results.length > 0 && (walkIns.length === 0 || tab === 'jobs')) || (results.length === 0 && walkIns.length === 0)) && (
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
            <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'12px 18px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'14px'}}>{field === 'rego' && walkIns.length + results.length > 0 ? 'Yard jobs' : 'Search Results'}</div>
              <span style={{background:'var(--blue-light)',color:'var(--blue)',fontSize:'11px',fontWeight:'500',padding:'3px 10px',borderRadius:'20px'}}>{results.length} found</span>
            </div>
            {results.length === 0 ? (
              <div style={{padding:'60px',textAlign:'center'}}>
                <div style={{fontSize:'2rem',marginBottom:'12px'}}>🔍</div>
                <div style={{color:'var(--blue)',fontWeight:'500',marginBottom:'6px'}}>No results found</div>
                <div style={{color:'var(--muted)',fontSize:'13px'}}>Try a different search term or field</div>
              </div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:'700px'}}>
                  <thead>
                    <tr>
                      {['Date','Location','Stock #','VIN','Rego','Dealership','Services','Extra Notes','Price','PO','Invoice','Status','Action'].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'1px',color:'var(--muted)',borderBottom:'1px solid var(--border)',background:'var(--bg)',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((job,i)=>{
                      const st = STATUS_BADGE[job.status] || STATUS_BADGE.submitted;
                      return (
                        <tr key={job._id}
                          onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                          onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'12px',color:'var(--muted)'}}>{new Date(job.createdAt).toLocaleDateString('en-AU')}</span></td>
                          <td style={td}><span style={{fontSize:'12px',color:'var(--blue)'}}>{job.location||'—'}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--blue)'}}>{job.stockNum}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'12px'}}>{job.vinNum||'—'}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500'}}>{job.rego||'—'}</span></td>
                          <td style={td}><span style={{fontSize:'13px',color:'var(--blue)',fontWeight:'500'}}>{job.dealership}</span></td>
                          <td style={td}><span style={{fontSize:'12px'}}>{(job.coreServices||[]).join(', ')||'—'}</span></td>
                          <td style={td}><span style={{fontSize:'12px',color:'var(--muted)'}}>{job.extraNotes||'—'}</span></td>
                          <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--green)'}}>${(job.price||0).toFixed(2)}</span></td>
                          <td style={td}>
                            {job.poNumber
                              ? <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'13px',color:'var(--green)',fontWeight:'500'}}>{job.poNumber}</span>
                              : <span style={{background:'var(--amber-bg)',color:'var(--amber)',fontSize:'11px',fontWeight:'500',padding:'2px 8px',borderRadius:'20px'}}>PENDING</span>}
                          </td>
                          <td style={td}>
                            {job.invoiceNumber
                              ? <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'12px',fontWeight:'600',color:'var(--purple)'}}>{job.invoiceNumber}</span>
                              : <span style={{color:'var(--hint)'}}>—</span>}
                          </td>
                          <td style={td}><span style={{background:st.bg,color:st.color,padding:'3px 9px',borderRadius:'20px',fontSize:'11px',fontWeight:'500'}}>{st.label}</span></td>
                          <td style={td}>
                            <button onClick={()=>navigate(`/jobs/${job._id}/edit`)} style={{background:'transparent',border:'1px solid rgba(217,119,6,0.3)',color:'var(--amber)',borderRadius:'6px',padding:'4px 10px',fontSize:'11px',cursor:'pointer',fontFamily:'inherit'}}>✏️ Edit</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Walk-ins matching the rego */}
        {searched && field === 'rego' && walkIns.length > 0 && (results.length === 0 || tab === 'walkins') && (
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden',boxShadow:'var(--shadow-sm)',marginTop:'16px'}}>
            <div style={{background:'var(--bg)',borderBottom:'1px solid var(--border)',padding:'12px 18px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{color:'var(--blue)',fontWeight:'500',fontSize:'14px'}}>Walk-ins</div>
              <span style={{background:'var(--blue-light)',color:'var(--blue)',fontSize:'11px',fontWeight:'500',padding:'3px 10px',borderRadius:'20px'}}>{walkIns.length} found</span>
            </div>
            {walkIns.length === 0 ? (
              <div style={{padding:'28px',textAlign:'center',color:'var(--muted)',fontSize:'13px'}}>No walk-ins found for this rego</div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:'640px'}}>
                  <thead>
                    <tr>
                      {['Date','Customer','Rego','Services','Price','Status','Invoice','Action'].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:'11px',fontWeight:'500',textTransform:'uppercase',letterSpacing:'1px',color:'var(--muted)',borderBottom:'1px solid var(--border)',background:'var(--bg)',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {walkIns.map(w=>(
                      <tr key={w._id} onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'} onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                        <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontSize:'12px',color:'var(--muted)'}}>{w.date ? new Date(w.date).toLocaleDateString('en-AU') : '—'}</span></td>
                        <td style={td}><div style={{fontWeight:'500',fontSize:'13px'}}>{w.customerName||'—'}</div><div style={{fontSize:'11px',color:'var(--muted)'}}>{w.phone||''}</div></td>
                        <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--blue)'}}>{w.rego||'—'}</span></td>
                        <td style={td}>{(w.services?.length ? w.services : [w.service]).map(sv => <div key={sv} style={{fontSize:'12px'}}>{sv}</div>)}</td>
                        <td style={td}><span style={{fontFamily:'JetBrains Mono,monospace',fontWeight:'500',color:'var(--green)'}}>${(w.price||0).toFixed(2)}</span></td>
                        <td style={td}><span style={{fontSize:'12px',textTransform:'capitalize',fontWeight:w.status==='paid'?600:400,color:w.status==='paid'?'var(--green)':'inherit'}}>{w.status==='paid'?'✓ Paid':w.status}</span></td>
                        <td style={td}>
                          <button onClick={()=>setInvoiceFor(w)} style={{background:'transparent',border:'1px solid rgba(10,110,180,0.3)',color:'var(--blue)',borderRadius:'6px',padding:'4px 10px',fontSize:'11px',cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
                            🧾 {w.invoice ? w.invoice.number : 'Invoice'}
                          </button>
                        </td>
                        <td style={td}>
                          <button onClick={()=>navigate(`/walkins/${w._id}/edit`)} style={{background:'transparent',border:'1px solid rgba(217,119,6,0.3)',color:'var(--amber)',borderRadius:'6px',padding:'4px 10px',fontSize:'11px',cursor:'pointer',fontFamily:'inherit'}}>✏️ Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {invoiceFor && (
        <SendInvoiceModal walkIn={invoiceFor} defaultSite={user?.selectedLocation}
          onClose={() => setInvoiceFor(null)}
          onWalkInChanged={(w) => setWalkIns(prev => prev.map(x => x._id===w._id ? { ...x, ...w } : x))}
          onSent={(invoice, email) => setWalkIns(prev => prev.map(w => w._id===invoiceFor._id ? { ...w, invoice, customerEmail: email } : w))}/>
      )}
    </div>
  );
}

const backBtn = {background:'var(--bg)',border:'1px solid var(--border)',color:'var(--muted)',borderRadius:'var(--radius)',padding:'6px 14px',cursor:'pointer',fontSize:'13px',fontFamily:'inherit'};
const td = {padding:'11px 14px',borderBottom:'1px solid rgba(0,0,0,0.04)',verticalAlign:'middle'};
